import { useEffect, useState } from "react";
import { TICKER_SYMBOLS, TICKER_REFRESH_MS, REVEAL_THRESHOLD } from "./constants";

/**
 * Adds .reveal-in to .reveal elements as they enter the viewport.
 * Mount-once: only module-scope constants are referenced, so the empty
 * dependency array is exhaustive-deps compliant.
 */
export const useRevealOnScroll = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
          }
        });
      },
      { threshold: REVEAL_THRESHOLD }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // Mount-once effect: `IntersectionObserver` is a browser global,
    // `REVEAL_THRESHOLD` is a module-level constant (stable across renders),
    // and `el` is a loop-local variable inside forEach. There are no reactive
    // dependencies to declare.
  }, []);
};

const toTickerRow = (quotes) => (sym) => {
  const q = quotes[sym];
  if (!q) return { sym, price: null, chg: null, dir: "muted" };
  return {
    sym,
    price: q.price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    chg: `${q.changePercent >= 0 ? "+" : ""}${q.changePercent.toFixed(2)}%`,
    dir: q.changePercent >= 0 ? "up" : "down",
  };
};

/**
 * Polls the live-quote proxy every TICKER_REFRESH_MS. On any fetch/network
 * failure the last good snapshot stays visible (fail-quiet by design).
 */
export const useTickerQuotes = () => {
  const [ticker, setTicker] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchTicker = async () => {
      try {
        const res = await fetch(`/api/market/quotes?symbols=${TICKER_SYMBOLS.join(",")}`, { cache: "no-store" });
        if (!res.ok) return; // non-ok response — keep last good snapshot
        const data = await res.json();
        if (!cancelled) {
          setTicker(TICKER_SYMBOLS.map(toTickerRow(data.quotes || {})));
        }
      } catch {
        // Network hiccup — keep last good ticker snapshot visible (fail-quiet).
      }
    };

    fetchTicker();
    const id = setInterval(fetchTicker, TICKER_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // Mount-once effect: `TICKER_SYMBOLS` and `TICKER_REFRESH_MS` are
    // module-level constants (stable), and `cancelled` is declared inside
    // this effect. `setTicker` is React-guaranteed stable. There are no
    // reactive dependencies to declare — the interval must fire exactly once.
  }, []);

  return ticker;
};
